import React, { useRef, useState } from 'react';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import { StationeryProductPricingContextProvider, useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { PDP_SUPPLEMENTARY_URLS } from '@apps/card/constants/pdpSupplementaryImages';
import type { DraftData } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { DraftJson } from '@apps/card/routes/CardCustomizer/DraftJson.types';
import { CardFormat } from '@apps/card/routes/Dashboard/components/ProductDetailsPage/constants';
import { AssetContainer } from '@apps/card/routes/Dashboard/components/ProductDetailsPage/components/GenericPreviewPane';
import { PdpLayout } from '@apps/card/routes/Dashboard/components/ProductDetailsPage/components/PdpLayout/PdpLayout';
import { CardCustomizationPane } from '@apps/card/routes/Dashboard/components/ProductDetailsPage/components/CardCustomizationContent/CardCustomizationPane';
import { useStickyCustomizeButton } from '@apps/card/routes/Dashboard/components/ProductDetailsPage/useStickyCustomizeButton';

/**
 * The carrier's stored dimensions, which nothing on this page reads.
 *
 * `StationeryProductSelector` wants a template with measurements, and the carrier row's own are an
 * accidental column default that nothing may key off. Satisfying that type is
 * now their only job: since PR 7b both the row's label and its glyph key on `productType`, and the
 * picker's remaining reads of them sit in its switch-telemetry path, which cannot fire here — the
 * envelope product is the only product on this template, so there is nothing to switch to.
 */
const CARRIER_PICKER_DIMENSIONS = { widthInches: 5, heightInches: 7 } as const;

type EnvelopePdpProps = Readonly<{
  carrierId: string;
  carrierThemeId: string;
  /** The carrier blob, verbatim. Seeds the pane's local customization scope. */
  carrierJson: DraftJson;
  product: StationeryProduct;
  isCreatingDraft: boolean;
  /** Called with the quantity chosen on this page; creation itself lives in `useStartEnvelopeOrder`. */
  onCustomizeClick: (quantity: number) => void;
}>;

const Root = (props: Omit<EnvelopePdpProps, 'product'>) => {
  const { carrierId, carrierThemeId, carrierJson, isCreatingDraft, onCustomizeClick } = props;
  const { tq } = useTranslation('stationery');
  const isMobileOrTablet = useIsMobileOrTablet();
  // The context's product, not the raw one: only the UI-mapped copy carries `translations`.
  const { activeProduct } = useStationeryProductPricingContext();

  // The quantity the shopper picks lives in the pane's local customization scope until Customize
  // Now is pressed — there is no draft to write it to yet. Lifted out the way `ProductDetailsPage`
  // lifts the paper PDP's config, so the button can send it with the creation call.
  const [quantity, setQuantity] = useState(carrierJson.customizations.quantity);
  const handleCustomization = useEventCallback((draft: DraftData) => setQuantity(draft.config.customizations.quantity));

  const intersectionRef = useRef<HTMLButtonElement>(null);
  const showCustomizeButtonInHeader = useStickyCustomizeButton(intersectionRef, !isMobileOrTablet);

  const handleCustomizeNowClick = useEventCallback(() => onCustomizeClick(quantity));

  const designName = activeProduct.translations.displayName || tq`Envelopes`;

  return (
    <PdpLayout
      themeId={carrierThemeId}
      designName={designName}
      templateCategory={StationeryTemplateCategoryEnum.envelope}
      isCreatingDraft={isCreatingDraft}
      showCustomizeButtonInHeader={showCustomizeButtonInHeader}
      onCustomizeNowClick={handleCustomizeNowClick}
      productSelector={{
        cardFormat: CardFormat.paper,
        productId: activeProduct.id,
        currentTemplate: { id: carrierId, ...CARRIER_PICKER_DIMENSIONS },
        // One product on one template: the picker renders a single, already-selected row, so
        // there is nothing for a change handler to switch to.
        onChange: () => undefined,
      }}
      previewPane={() => (
        <AssetContainer
          enforceAspectRatio
          label={tq`Free Recipient & Return Addressing`}
          backgroundImageUrl={PDP_SUPPLEMENTARY_URLS.ENVELOPES_COMMON}
        />
      )}
      customizationPane={() => (
        <CardCustomizationPane
          hideCustomizeNowButton={isMobileOrTablet}
          intersectionRef={intersectionRef}
          customizationConfig={carrierJson}
          isCreatingDraft={isCreatingDraft}
          templateId={carrierId}
          themeId={carrierThemeId}
          themeName={designName}
          stationeryTemplateCategory={StationeryTemplateCategoryEnum.envelope}
          onCardCustomizeClick={handleCustomizeNowClick}
          onCardCustomization={handleCustomization}
        />
      )}
    />
  );
};

/**
 * `/cards/envelopes` — the product details page for standalone envelopes.
 *
 * Composes the same `PdpLayout` every card PDP uses, with the same `CardCustomizationPane`. The
 * page is deliberately NOT routed through `ProductDetailsPage`: that shell resolves its template
 * from the gallery by theme and category (the carrier is excluded from it by design), and
 * `usePdpData` dereferences `themeJSON.card.front.dimensions` unguarded, which an envelope draft
 * does not have and must never be given. This component plays the part its `Root` plays —
 * supply the product, hold the pricing context — and nothing more.
 *
 * Everything card-shaped inside the layout removes itself: the colorway swatches, the orientation
 * picker, the foil upsell and the related-items strip each render null without a card front, size
 * siblings, a foil counterpart or a shared theme.
 */
export const EnvelopePdp = ({ product, ...rest }: EnvelopePdpProps) => (
  <StationeryProductPricingContextProvider
    products={[product]}
    activeProductId={product.id}
  >
    <Root {...rest} />
  </StationeryProductPricingContextProvider>
);
